import { useState, useEffect } from "react";
import { Pressable, ActivityIndicator } from "react-native";
import styled from "styled-components/native";
import KeyboardAwareScroll from "@/components/KeyboardAwareScroll";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useClub } from "@/contexts/ClubContext";
import { useRouter } from "expo-router";
import { colors, radius, font } from "@/lib/theme";
import ContentWidth from "@/components/ContentWidth";

const Section = styled.View`
  padding: 20px 20px 0;
`;

const SectionLabel = styled.Text`
  font-size: 12px;
  font-weight: ${font.extrabold};
  color: ${colors.inkMuted};
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 12px;
`;

const HintText = styled.Text`
  font-size: 13px;
  color: ${colors.inkMuted};
  line-height: 20px;
  margin-bottom: 12px;
`;

const Card = styled.View`
  background-color: ${colors.white};
  border-radius: ${radius.lg}px;
  border: 1px solid ${colors.line};
  overflow: hidden;
`;

const Row = styled.View<{ bordered?: boolean }>`
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  ${(p) => p.bordered ? `border-bottom-width: 1px; border-bottom-color: ${colors.line};` : ""}
`;

const RowLabel = styled.Text`
  font-size: 14px;
  font-weight: ${font.bold};
  color: ${colors.ink};
  flex: 1;
`;

const RowSub = styled.Text`
  font-size: 12px;
  color: ${colors.inkMuted};
  font-weight: ${font.medium};
  margin-top: 2px;
`;

const AmountInput = styled.TextInput`
  background-color: ${colors.bg};
  border: 1px solid ${colors.line};
  border-radius: ${radius.md}px;
  padding: 8px 12px;
  font-size: 16px;
  font-weight: ${font.bold};
  color: ${colors.ink};
  width: 80px;
  text-align: center;
`;

const CurrencyLabel = styled.Text`
  font-size: 14px;
  color: ${colors.inkMuted};
  font-weight: ${font.bold};
  margin-right: 6px;
`;

const AmountWrap = styled.View`
  flex-direction: row;
  align-items: center;
`;

const SaveBtn = styled.View`
  background-color: ${colors.brand};
  border-radius: ${radius.card}px;
  padding: 16px;
  align-items: center;
  margin: 24px 20px;
`;

const SaveBtnText = styled.Text`
  color: #fff;
  font-size: 16px;
  font-weight: ${font.bold};
`;

const LoadingContainer = styled.View`
  flex: 1;
  background-color: ${colors.bg};
  justify-content: center;
  align-items: center;
`;

const Spacer = styled.View`
  height: 32px;
`;

export default function FeeSettingsScreen() {
  const router = useRouter();
  const { club } = useClub();
  const [defaultFee, setDefaultFee] = useState("");
  const [defaultJuniorFee, setDefaultJuniorFee] = useState("");
  const [teamFees, setTeamFees] = useState<Record<string, string>>({});
  const [juniorFees, setJuniorFees] = useState<Record<string, string>>({});
  const [adultMembership, setAdultMembership] = useState("");
  const [juniorMembership, setJuniorMembership] = useState("");
  const [maxInstallments, setMaxInstallments] = useState("4");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!club?.id) return;
    async function load() {
      const snap = await getDoc(doc(db, "clubs", club!.id, "settings", "fees"));
      if (snap.exists()) {
        const data = snap.data();
        setDefaultFee(String(data.defaultMatchFee || ""));
        setDefaultJuniorFee(String(data.defaultJuniorFee || ""));
        setAdultMembership(String(data.adultMembership || ""));
        setJuniorMembership(String(data.juniorMembership || ""));
        setMaxInstallments(String(data.maxInstallments || "4"));
        if (data.feesByTeam) {
          const fees: Record<string, string> = {};
          for (const [team, amount] of Object.entries(data.feesByTeam)) {
            fees[team] = String(amount);
          }
          setTeamFees(fees);
        }
        if (data.juniorFeesByTeam) {
          const fees: Record<string, string> = {};
          for (const [team, amount] of Object.entries(data.juniorFeesByTeam)) {
            fees[team] = String(amount);
          }
          setJuniorFees(fees);
        }
      }
      setLoading(false);
    }
    load();
  }, [club?.id]);

  const handleSave = async () => {
    if (!club) return;
    setSaving(true);

    const feesByTeam: Record<string, number> = {};
    for (const [team, amount] of Object.entries(teamFees)) {
      const num = parseFloat(amount);
      if (!isNaN(num) && num > 0) feesByTeam[team] = num;
    }

    const juniorFeesByTeam: Record<string, number> = {};
    for (const [team, amount] of Object.entries(juniorFees)) {
      const num = parseFloat(amount);
      if (!isNaN(num) && num > 0) juniorFeesByTeam[team] = num;
    }

    await setDoc(doc(db, "clubs", club.id, "settings", "fees"), {
      defaultMatchFee: parseFloat(defaultFee) || 0,
      defaultJuniorFee: parseFloat(defaultJuniorFee) || 0,
      feesByTeam,
      juniorFeesByTeam,
      adultMembership: parseFloat(adultMembership) || 0,
      juniorMembership: parseFloat(juniorMembership) || 0,
      maxInstallments: parseInt(maxInstallments) || 4,
    });
    setSaving(false);
    router.back();
  };

  if (loading) {
    return <LoadingContainer><ActivityIndicator size="large" color={colors.brand} /></LoadingContainer>;
  }

  const teams = club?.teams || [];
  const seniorTeams = teams.filter((t) => !t.toLowerCase().includes("junior") && !t.toLowerCase().includes("u1") && !t.toLowerCase().includes("u9"));
  const juniorTeams = teams.filter((t) => t.toLowerCase().includes("junior") || t.toLowerCase().includes("u1") || t.toLowerCase().includes("u9"));

  return (
    <KeyboardAwareScroll style={{ flex: 1, backgroundColor: colors.bg }} showsVerticalScrollIndicator={false}>
      <ContentWidth>
        {/* Default fees */}
        <Section>
          <SectionLabel>Default Fees</SectionLabel>
          <HintText>Set the default fee per player per match.</HintText>
          <Card>
            <Row bordered>
              <RowLabel>Senior match fee</RowLabel>
              <AmountWrap>
                <CurrencyLabel>£</CurrencyLabel>
                <AmountInput
                  value={defaultFee}
                  onChangeText={setDefaultFee}
                  keyboardType="numeric"
                  placeholder="0"
                  placeholderTextColor={colors.inkMuted}
                />
              </AmountWrap>
            </Row>
            <Row>
              <RowLabel>Junior match fee</RowLabel>
              <AmountWrap>
                <CurrencyLabel>£</CurrencyLabel>
                <AmountInput
                  value={defaultJuniorFee}
                  onChangeText={setDefaultJuniorFee}
                  keyboardType="numeric"
                  placeholder="0"
                  placeholderTextColor={colors.inkMuted}
                />
              </AmountWrap>
            </Row>
          </Card>
        </Section>

        {/* Senior team overrides */}
        {seniorTeams.length > 0 && (
          <Section>
            <SectionLabel>Senior Team Overrides</SectionLabel>
            <HintText>Leave blank to use the default senior fee.</HintText>
            <Card>
              {seniorTeams.map((team, i) => (
                <Row key={team} bordered={i < seniorTeams.length - 1}>
                  <RowLabel>{team}</RowLabel>
                  <AmountWrap>
                    <CurrencyLabel>£</CurrencyLabel>
                    <AmountInput
                      value={teamFees[team] || ""}
                      onChangeText={(v) => setTeamFees({ ...teamFees, [team]: v })}
                      keyboardType="numeric"
                      placeholder={defaultFee || "0"}
                      placeholderTextColor={colors.inkMuted}
                    />
                  </AmountWrap>
                </Row>
              ))}
            </Card>
          </Section>
        )}

        {/* Junior team overrides */}
        {juniorTeams.length > 0 && (
          <Section>
            <SectionLabel>Junior Team Overrides</SectionLabel>
            <HintText>Leave blank to use the default junior fee.</HintText>
            <Card>
              {juniorTeams.map((team, i) => (
                <Row key={team} bordered={i < juniorTeams.length - 1}>
                  <RowLabel>{team}</RowLabel>
                  <AmountWrap>
                    <CurrencyLabel>£</CurrencyLabel>
                    <AmountInput
                      value={juniorFees[team] || ""}
                      onChangeText={(v) => setJuniorFees({ ...juniorFees, [team]: v })}
                      keyboardType="numeric"
                      placeholder={defaultJuniorFee || "0"}
                      placeholderTextColor={colors.inkMuted}
                    />
                  </AmountWrap>
                </Row>
              ))}
            </Card>
          </Section>
        )}

        {/* Membership fees */}
        <Section>
          <SectionLabel>Annual Membership</SectionLabel>
          <HintText>Set the annual membership fee for adults and juniors.</HintText>
          <Card>
            <Row bordered>
              <RowLabel>Adult membership</RowLabel>
              <AmountWrap>
                <CurrencyLabel>£</CurrencyLabel>
                <AmountInput
                  value={adultMembership}
                  onChangeText={setAdultMembership}
                  keyboardType="numeric"
                  placeholder="200"
                  placeholderTextColor={colors.inkMuted}
                />
              </AmountWrap>
            </Row>
            <Row bordered>
              <RowLabel>Junior membership</RowLabel>
              <AmountWrap>
                <CurrencyLabel>£</CurrencyLabel>
                <AmountInput
                  value={juniorMembership}
                  onChangeText={setJuniorMembership}
                  keyboardType="numeric"
                  placeholder="100"
                  placeholderTextColor={colors.inkMuted}
                />
              </AmountWrap>
            </Row>
            <Row>
              <RowLabel>Max installments</RowLabel>
              <AmountInput
                value={maxInstallments}
                onChangeText={setMaxInstallments}
                keyboardType="numeric"
                placeholder="4"
                placeholderTextColor={colors.inkMuted}
              />
            </Row>
          </Card>
          <HintText>Admin can set 1-{maxInstallments || "4"} installments per member. Default is pay in full.</HintText>
        </Section>

        <Pressable style={({ pressed }) => ({ opacity: pressed ? 0.9 : 1 })} onPress={handleSave} disabled={saving}>
          <SaveBtn>
            {saving ? <ActivityIndicator color="#fff" /> : <SaveBtnText>Save</SaveBtnText>}
          </SaveBtn>
        </Pressable>

        <Spacer />
      </ContentWidth>
    </KeyboardAwareScroll>
  );
}
